import { MediaType } from '@qse/api'
import { EmptyStateIcon, SearchFilter, toast } from '@qse/ui'
import { useSearchFilterVisibility } from '@qse/ui'
import { useIsMobile } from '@qse/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { FileExplorer } from '@/components'
import { useStableInView } from '@/hooks/useStableInView'

export const useHistoryTracker = () => {
  const location = useLocation()
  const historyStack = useRef<string[]>([])
  const renderCount = useRef(0)
  const lastPathname = useRef<string>('')

  useEffect(() => {
    renderCount.current += 1

    // Only add to history if pathname actually changed
    if (lastPathname.current !== location.pathname) {
      historyStack.current.push(location.pathname)
      lastPathname.current = location.pathname
    }
  }, [location])

  return historyStack.current
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface SearchValues {
  search: string
  fileType?: string
  isDir?: boolean
  createdFrom?: string
  createdTo?: string
}

const Overview = () => {
  const query = useQuery()
  const location = useLocation()
  const searchQuery = query.get('query') || ''
  const fileType = query.get('fileType') || ''
  const isDir = query.get('isDir') || ''
  const createdFrom = query.get('createdFrom') || ''
  const createdTo = query.get('createdTo') || ''

  useHistoryTracker()

  // Check if we should show the not found error state
  const showNotFoundError = location.state?.showNotFoundError
  const hasShownErrorToast = useRef(false)

  // Show toast notification when redirected due to 404 error
  useEffect(() => {
    if (showNotFoundError && !hasShownErrorToast.current) {
      hasShownErrorToast.current = true
      toast.error('Page not found', {
        description: "The page or resource you're looking for doesn't exist",
      })
    }
  }, [showNotFoundError])

  const [searchValues, setSearchValues] = useState<SearchValues>({
    search: searchQuery,
    fileType: fileType || undefined,
    isDir: isDir === 'true' ? true : undefined,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
  })

  const searchFilterRef = useRef<HTMLDivElement>(null)
  const setSearchFilterVisible = useSearchFilterVisibility(state => state.setVisible)
  const isMobile = useIsMobile()

  // Use stable inView detection to prevent mobile flickering
  const isInView = useStableInView(searchFilterRef, {
    amount: 'all',
    once: false,
  })

  // Update visibility state
  useEffect(() => {
    setSearchFilterVisible(isInView)
  }, [isInView, setSearchFilterVisible])

  // Search parameters for FileExplorer
  const searchParams = useMemo(
    () => ({
      limit: 50,
      search: searchValues.search || undefined,
      isDir: searchValues.isDir === true ? true : undefined,
      mediaType:
        searchValues.isDir === true ? undefined : (searchValues.fileType as MediaType) || undefined,
      createdFrom: searchValues.createdFrom || undefined,
      createdTo: searchValues.createdTo || undefined,
      location: 'HOME' as const, // Use location to filter for home files
    }),
    [searchValues],
  )

  const emptyStateConfig = {
    icon: <EmptyStateIcon />,
    title: showNotFoundError ? 'My Drive' : 'Drive is empty',
    description: showNotFoundError
      ? "The page or resource you're looking for doesn't exist"
      : undefined,
  }

  const handleSearch = useCallback((values: SearchValues) => {
    setSearchValues(values)
  }, [])

  // Generate title based on active filters
  const getTitle = () => {
    const parts = []

    if (searchValues.search) {
      parts.push(`Search: "${searchValues.search}"`)
    }

    if (searchValues.fileType) {
      const fileTypeLabels: Record<string, string> = {
        FOLDER: 'Folder',
        DOCUMENT: 'Document',
        IMAGE: 'Image',
        VIDEO: 'Video',
        AUDIO: 'Audio',
        SPREADSHEET: 'Spreadsheet',
        PRESENTATION: 'Presentation',
        PDF: 'PDF',
        ARCHIVE: 'Archive',
        OTHER: 'Other Files',
      }

      parts.push(`Type: ${fileTypeLabels[searchValues.fileType] || searchValues.fileType}`)
    }

    if (searchValues.createdFrom || searchValues.createdTo) {
      parts.push('Date Filtered')
    }

    return parts.length > 0 ? parts.join(' | ') : 'Folders & Files'
  }

  return (
    <div
      className="flex flex-col gap-3 text-gray-900 dark:text-white relative justify-center"
      style={{ minHeight: 'calc(100dvh - 300px)' }}
    >
      {/* Search and Filter Section */}
      <div
        ref={searchFilterRef}
        className="md:pt-6"
        style={{
          // Prevent layout shifts on mobile by maintaining consistent height
          minHeight: isMobile ? '200px' : 'auto',
          // Fast, smooth transitions for better UX
          transition: isMobile ? 'opacity 0.15s ease-out' : 'none',
        }}
      >
        <SearchFilter isInView={isInView} redirectToSearchResults={true} onSearch={handleSearch} />
      </div>

      <div className="flex-1">
        <FileExplorer
          emptyStateConfig={emptyStateConfig}
          isInView={isInView}
          parentId={undefined}
          searchParams={searchParams}
          title={getTitle()}
          uploadEnabled={true}
        />
      </div>
    </div>
  )
}

export default Overview
